import { useQuery } from '@tanstack/react-query';
import { DollarSign, Users, TrendingUp, AlertTriangle, CreditCard, Package, Clock, Tag } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number) { return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`; }
const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };

export default function RevenueDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-revenue'],
    queryFn: async () => { const r = await apiClient.get('/admin/revenue'); return r.data.data; },
  });

  const kpis = data?.kpis;
  const planDist = data?.planDistribution || [];
  const tenants = data?.tenants || [];
  const trialExpiring = data?.trialExpiring || [];
  const addons = data?.addons || [];
  const coupons = data?.coupons;
  const usage = data?.usageThisMonth;

  const pieData = planDist.map((p: any, i: number) => ({ name: p.plan, value: p.count, fill: COLORS[i % COLORS.length] }));
  const revenueData = planDist.filter((p: any) => p.revenue > 0).map((p: any, i: number) => ({ name: p.plan, revenue: p.revenue, fill: COLORS[i % COLORS.length] }));

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="h-6 w-6 text-green-500" />Revenue Dashboard</h1>
        <p className="text-sm text-muted-foreground">Platform-wide revenue, subscriptions, and usage metrics</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'MRR', value: fmtCurrency(kpis?.mrr || 0), color: 'text-green-500' },
          { label: 'ARR', value: fmtCurrency(kpis?.arr || 0), color: 'text-green-500' },
          { label: 'Total Tenants', value: kpis?.totalTenants || 0 },
          { label: 'Active', value: kpis?.activeTenants || 0, color: 'text-green-500' },
          { label: 'Trial', value: kpis?.trialTenants || 0, color: 'text-blue-500' },
          { label: 'Cancelled', value: kpis?.cancelledTenants || 0, color: (kpis?.cancelledTenants || 0) > 0 ? 'text-red-500' : '' },
          { label: 'Past Due', value: kpis?.pastDueTenants || 0, color: (kpis?.pastDueTenants || 0) > 0 ? 'text-yellow-500' : '' },
          { label: 'Total Revenue', value: fmtCurrency(kpis?.totalRevenue || 0), color: 'text-green-500' },
        ].map(kpi => (
          <Card key={kpi.label}><CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
            <p className={cn('text-lg font-bold', kpi.color)}>{kpi.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {pieData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Tenants by Plan</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={70} innerRadius={30} activeShape={null as any}>
                  {pieData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                </Pie><Tooltip {...TOOLTIP_STYLE} cursor={false}/><Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} /></PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {revenueData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">MRR by Plan</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => `$${v}`} />
                  <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [fmtCurrency(v), 'MRR']} />
                  <Bar dataKey="revenue" radius={[4, 4, 0, 0]} activeBar={false}>
                    {revenueData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Usage this month */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Platform Usage (This Month)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'AI Queries', value: usage?.aiQueries || 0, icon: '💬' },
              { label: 'Document Uploads', value: usage?.documentUploads || 0, icon: '📄' },
              { label: 'Reports Generated', value: usage?.reportsGenerated || 0, icon: '📊' },
              { label: 'API Calls', value: usage?.apiCalls || 0, icon: '🔗' },
            ].map(u => (
              <div key={u.label} className="flex items-center justify-between">
                <span className="text-xs">{u.icon} {u.label}</span>
                <span className="text-sm font-bold">{u.value.toLocaleString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Trial Expiring + Coupons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Clock className="h-4 w-4 text-yellow-500" />Trials Expiring Soon</CardTitle></CardHeader>
          <CardContent className="p-0">
            {trialExpiring.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No trials expiring</p>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {trialExpiring.map((t: any, i: number) => {
                    const daysLeft = Math.ceil((new Date(t.trial_ends_at).getTime() - Date.now()) / 86400000);
                    return (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2 font-medium">{t.name}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge variant={daysLeft <= 3 ? 'destructive' : 'secondary'} className="text-[9px]">
                            {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Tag className="h-4 w-4 text-purple-500" />Coupon Stats</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-xs"><span>Total Coupons</span><span className="font-bold">{coupons?.total_coupons || 0}</span></div>
            <div className="flex justify-between text-xs"><span>Active</span><span className="font-bold text-green-500">{coupons?.active_coupons || 0}</span></div>
            <div className="flex justify-between text-xs"><span>Total Redemptions</span><span className="font-bold">{coupons?.total_redemptions || 0}</span></div>
            {addons.length > 0 && (
              <>
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs font-semibold mb-1">Active Add-ons</p>
                  {addons.map((a: any, i: number) => (
                    <div key={i} className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{a.name} ({a.subscribers})</span>
                      <span>{fmtCurrency(parseFloat(a.addon_mrr))}/mo</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Tenants Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">All Tenants</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-muted/30">
                <th className="px-3 py-2 text-left font-medium">Tenant</th>
                <th className="px-3 py-2 text-left font-medium">Plan</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">MRR</th>
                <th className="px-3 py-2 text-right font-medium">Clients</th>
                <th className="px-3 py-2 text-right font-medium">Users</th>
                <th className="px-3 py-2 text-right font-medium">AI Queries</th>
                <th className="px-3 py-2 text-left font-medium">Joined</th>
              </tr></thead>
              <tbody>
                {tenants.map((t: any, i: number) => (
                  <tr key={i} className="border-b hover:bg-muted/20">
                    <td className="px-3 py-2 font-medium">{t.name}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{t.plan_name || 'None'}</Badge></td>
                    <td className="px-3 py-2">
                      <Badge variant={t.sub_status === 'active' ? 'default' : t.sub_status === 'trial' ? 'secondary' : 'destructive'} className="text-[9px]">{t.sub_status || 'No sub'}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">{t.price_monthly ? fmtCurrency(parseFloat(t.price_monthly)) : '-'}</td>
                    <td className="px-3 py-2 text-right">{t.client_count}</td>
                    <td className="px-3 py-2 text-right">{t.user_count}</td>
                    <td className="px-3 py-2 text-right">{t.ai_queries_this_month}</td>
                    <td className="px-3 py-2 text-muted-foreground">{new Date(t.created_at).toLocaleDateString('en-AU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
